const React = require('react');
const ControlledSelectField = require('./ControlledSelectField');
const FieldContext = require('./FieldContext');
const OptionUtils = require('./options');

const internals = {};

internals.normalizeRequiredLabel = ({
    label,
    required
}) => {

    if (!required || typeof label !== 'string') {
        return label;
    }

    return label.replace(/\s*\*+\s*$/, '');
};

module.exports = function FormSelectField(props) {

    const {
        field = {},
        fieldName = field.name,
        label = field.label,
        onBlur,
        onChange,
        onFocus,
        options = field.options || [],
        setValue,
        textFieldProps = {},
        useField: requestedUseFieldHook,
        value
    } = props;

    const resolvedUseField = typeof requestedUseFieldHook === 'function'
        ? requestedUseFieldHook
        : FieldContext.useField;

    const fieldController = resolvedUseField(fieldName);
    const formProps = fieldController.formProps || {};
    const resolvedOptions = typeof field.optionsSource === 'string' && Array.isArray(formProps[field.optionsSource])
        ? formProps[field.optionsSource]
        : options;

    if (field.hideWhenNoOptions && !resolvedOptions.length) {
        return null;
    }

    const resolvedTextFieldProps = fieldController.getTextFieldProps?.(textFieldProps) || textFieldProps;
    const hasError = !!resolvedTextFieldProps.error;
    const helperText = resolvedTextFieldProps.helperText;
    const required = !!(field.required || resolvedTextFieldProps.required);
    const resolvedValue = typeof value === 'undefined'
        ? fieldController.getValue()
        : value;
    const getSelectedOption = (selectedValue) => resolvedOptions.find((option) => {

        const optionValue = OptionUtils.getOptionValue(option);

        return optionValue === selectedValue || String(optionValue) === String(selectedValue);
    });
    const getSelectedLabel = (selectedValue) => {

        const selected = getSelectedOption(selectedValue);

        return selected ? OptionUtils.getOptionLabel(selected) : selectedValue;
    };
    const selectedOption = getSelectedOption(resolvedValue);
    const setFieldValue = typeof setValue === 'function'
        ? setValue
        : fieldController.setValue;
    const rendererContext = {
        field,
        formProps,
        options: resolvedOptions,
        selectedOption
    };
    const renderSelectedValue = (selectedValue) => {

        if (typeof field.renderValue === 'function') {
            return field.renderValue({
                ...rendererContext,
                selectedOption: getSelectedOption(selectedValue),
                value: selectedValue
            });
        }

        if (Array.isArray(selectedValue)) {
            return selectedValue.map(getSelectedLabel).join(', ');
        }

        return getSelectedLabel(selectedValue);
    };

    return <ControlledSelectField
        label={internals.normalizeRequiredLabel({
            label,
            required
        })}
        options={resolvedOptions}
        renderOption={typeof field.renderOption === 'function'
            ? (option) => field.renderOption({
                ...rendererContext,
                option
            })
            : undefined}
        renderValue={renderSelectedValue}
        value={resolvedValue}
        onBlur={(event) => {
            fieldController.onBlur?.(event);
            onBlur?.(event);
        }}
        onChange={(nextValue, event) => {

            setFieldValue(nextValue, event);

            if (typeof onChange === 'function') {
                onChange(nextValue, event);
            }
        }}
        onFocus={(event) => {
            fieldController.onFocus?.(event);
            onFocus?.(event);
        }}
        textFieldProps={{
            ...resolvedTextFieldProps,
            required,
            error: !!hasError,
            helperText,
            inputProps: {
                ...(resolvedTextFieldProps.inputProps || {}),
                'aria-invalid': hasError || undefined
            }
        }}
    />;
};

module.exports.internals = internals;
